/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/require-default-props */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { shallowEqual } from 'recompose';
import FormikTree from 'src/components/formik/FormikTree';
import { Formik, Form } from 'formik';
import { withFormikDevtools } from 'formik-devtools-extension';
import { isProduction } from 'src/config/index';
import PropTypes from 'prop-types';
import FormikInput from 'src/components/formik/FormikInput';
import FormikCreateSearchClear from 'src/components/formik/FormikCreateSearchClear';
import { DATE_TIME_FORMAT, REQUEST_STATUS } from 'src/constants/CommonConstants';
import { usePrevious } from 'src/utils/ReactHooksUtil';
import ScrollInViewHOC from 'src/hoc/ScrollHOC';
import { permissionsToTreeStructure } from '../../utils/CommonUtils';
import { permissionMasterFetch } from './actions/PermissionsMasterActions';
import {
    rolePermissionsEditAction, rolePermissionsResetAction, rolePermissionsSearchAction, rolePermissionsUpsertAction,
} from './actions/RolePermissionsActions';
import { RolePermissionsDefaultProps, RolePermissionsYupSchema } from './PermissionPropTypes';
import SearchContainer from '../search-container/SearchContainer';

const Render = (props) => {
    // DEV TOOL
    if (!isProduction()) {
        withFormikDevtools(props);
    }

    const {
        // errors,
        // touched,
        // enableDisableAction,
        onClear,
        forceRefreshSearchContainer,
        resetForm,
        isSubmitting,
        values,
        scopeTree,
    } = props;

    return (
        <div>
            <ScrollInViewHOC
                changeDetection={{
                    id: values._id,
                }}
                scrollOnMount={false}
            >
                <div>
                    <Form autoComplete="disabled">
                        <div className="card mt-3">
                            <div className="card-body">
                                <h5 className="card-title">Add Permissions to Role</h5>

                                <div className="form-group col">
                                    <label>Role Title</label>
                                    <FormikInput
                                        name="roleTitle"
                                        className="form-control"
                                    />
                                </div>

                                <div className="form-group col">
                                    <FormikTree
                                        nodes={scopeTree}
                                        name="permissions"
                                        checked={values.permissions}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="text-center py-4 mt-3">
                            <FormikCreateSearchClear
                                isSubmitting={isSubmitting}
                                isUpdate={Boolean(values._id)}
                                onClear={onClear}
                                resetForm={resetForm}
                                values={values}
                                submitScope="people.role_manager.manage.submit"
                            />
                        </div>
                    </Form>
                </div>
            </ScrollInViewHOC>

            <SearchContainer
                forceRefreshSearchContainer={forceRefreshSearchContainer}
                reducer="RolePermissionsReducer"
                dataPath="search"
                action={rolePermissionsSearchAction}
                editAction={rolePermissionsEditAction}
                hasEdit
                columns={{
                    roleTitle: {
                        display: 'Title',
                    },
                    by: {
                        display: 'By',
                        path: 'by.personalInfo.firstName',
                    },
                    updatedBy: {
                        display: 'Updated By',
                        path: 'updatedBy.personalInfo.firstName',
                    },
                    ts: {
                        display: 'Ts',
                        type: Date,
                        format: DATE_TIME_FORMAT.TIMEAGO,
                    },
                    updatedTs: {
                        display: 'Updated Ts',
                        type: Date,
                        format: DATE_TIME_FORMAT.TIMEAGO,
                    },
                }}
            />
        </div>
    );
};

Render.propTypes = {
    scopeTree: PropTypes.array.isRequired,
    values: PropTypes.shape({
        permissions: PropTypes.array,
    }),
    resetForm: PropTypes.func.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    forceRefreshSearchContainer: PropTypes.object.isRequired,
    onClear: PropTypes.func.isRequired,
};

// setting submit button state after submitting
let setSubmitting = () => { };

const RolePermissionsForm = () => {
    // REDUX
    const dispatch = useDispatch();

    const {
        scopes,
        permissionMasterFetchReqStatus,
    } = useSelector((state) => state.PermissionsMasterReducer, shallowEqual);

    // ROLE RECORD
    const {
        currentRecord,
        upsertReqStatus,
    } = useSelector((state) => state.RolePermissionsReducer, shallowEqual);

    // ENABLE SUBMIT AFTER XHR REQUEST
    usePrevious({
        currentRecord,
        upsertReqStatus,
    }, (prev) => {
        if (
            prev.currentRecord !== currentRecord
            || (upsertReqStatus && prev.upsertReqStatus !== upsertReqStatus && upsertReqStatus !== REQUEST_STATUS.PENDING)) {
            setSubmitting(false);
        }
    });

    // FORCE REFRESH
    const [
        forceRefreshSearchContainer,
        setForceRefreshSearchContainer,
    ] = useState({});

    usePrevious({ upsertReqStatus }, (preState) => {
        if (preState.upsertReqStatus !== upsertReqStatus && upsertReqStatus === REQUEST_STATUS.SUCCESS) {
            setForceRefreshSearchContainer({});
        }
    });

    useEffect(() => {
        // fetch all permission if not in store
        if (!scopes && !permissionMasterFetchReqStatus) {
            dispatch(permissionMasterFetch({
                where: {},
                currentPage: 0,
                autopopulate: false,
                fullTextSearch: false,
            }));
        }
    }, [
        dispatch,
        scopes,
        permissionMasterFetchReqStatus,
    ]);

    // create structure for tree
    const scopeTree = useMemo(() => permissionsToTreeStructure(scopes), [
        scopes,
    ]);

    // SUBMIT
    const onSubmit = useCallback((
        values,
        formikUtils,
    ) => {
        ({ setSubmitting } = formikUtils);
        dispatch(rolePermissionsUpsertAction(values));
    }, [
        dispatch,
    ]);

    // CLEAR
    const onClear = useCallback(() => {
        dispatch(rolePermissionsResetAction());
    }, [
        dispatch,
    ]);

    return (
        <Formik
            onSubmit={onSubmit}
            enableReinitialize
            initialValues={currentRecord || RolePermissionsDefaultProps}
            validationSchema={RolePermissionsYupSchema}
        >
            {(formikProps) => (
                <Render
                    onClear={onClear}
                    scopeTree={scopeTree}
                    forceRefreshSearchContainer={forceRefreshSearchContainer}
                    {...formikProps}
                />
            )}
        </Formik>
    );
};

export default RolePermissionsForm;
